import { Component, Input, Output, EventEmitter, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Address } from '@shared';
import { AuthService } from '../services/auth.service';
import { AddressFormComponent } from '../address-form/address-form.component';

@Component({
  selector: 'app-address-list-selector',
  standalone: true,
  imports: [CommonModule, AddressFormComponent],
  template: `
    <div class="flex flex-col gap-4">
      <!-- Adress-Auswahl Modus (nur wenn "Same As" Option aktiviert ist) -->
      @if (showSameAsOption) {
        <div class="flex flex-col gap-3">
          <!-- Option 1: Gleiche Adresse verwenden -->
          <label 
            class="radio-button-container"
            [class.selected]="useSameAddress()"
            (click)="onUseSameAddress()">
            <input 
              type="radio" 
              name="delivery-address-mode"
              [checked]="useSameAddress()"
              (change)="onUseSameAddress()"
              class="cursor-pointer">
            <span class="flex-1 cursor-pointer">Gleiche Adresse wie oben verwenden</span>
          </label>

          <!-- Option 2: Andere Adresse wählen -->
          <label 
            class="radio-button-container"
            [class.selected]="!useSameAddress()"
            (click)="onSelectDifferentAddress()">
            <input 
              type="radio" 
              name="delivery-address-mode"
              [checked]="!useSameAddress()"
              (change)="onSelectDifferentAddress()"
              class="cursor-pointer">
            <span class="flex-1 cursor-pointer">Andere Adresse wählen</span>
          </label>
        </div>
      }

      <!-- Gespeicherte Adressen -->
      @if (addresses.length > 0 && !useSameAddress()) {
        <div class="flex flex-col gap-3" role="radiogroup" aria-label="Lieferadresse wählen">
          @for (address of addresses; track address.id) {
            <label 
              class="radio-button-container"
              [class.selected]="selectedAddress?.id === address.id"
              (click)="onSelectAddress(address)">
              <input 
                type="radio" 
                name="delivery-address"
                [checked]="selectedAddress?.id === address.id"
                (change)="onSelectAddress(address)"
                class="cursor-pointer">
              <div class="flex-1 cursor-pointer">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium">{{ address.name }} {{ address.surname }}</span>
                  @if (address.address_type === 'default') {
                    <span class="badge-standard">Standard</span>
                  }
                </div>
                <div class="text-sm text-benzGrey-700">
                  {{ address.street }} {{ address.street_nr }}, {{ address.zipcode }} {{ address.city }}
                </div>
              </div>
            </label>
          }
        </div>
      }

      <!-- Keine Adressen vorhanden -->
      @if (addresses.length === 0 && !useSameAddress() && !showAddForm()) {
        <div class="p-6 border-2 border-dashed border-benzGrey-300 rounded-lg text-center">
          <p class="text-benzGrey-700 mb-3">Keine gespeicherten Adressen vorhanden</p>
          <button 
            type="button"
            (click)="showAddForm.set(true)"
            class="px-4 py-2 bg-benzRed-700 text-white rounded-lg hover:bg-benzRed-800 transition-colors">
            Erste Adresse hinzufügen
          </button>
        </div>
      }

      <!-- Neue Adresse hinzufügen Button -->
      @if (addresses.length > 0 && !useSameAddress() && !showAddForm()) {
        <button 
          type="button"
          (click)="showAddForm.set(true)"
          class="w-full p-4 border-2 border-dashed border-benzGrey-400 rounded-lg text-benzGrey-700 hover:border-benzRed-700 hover:text-benzRed-700 hover:bg-benzRed-50 transition-all">
          + Neue Adresse hinzufügen
        </button>
      }

      <!-- Formular für neue Adresse -->
      @if (showAddForm() && !useSameAddress()) {
        <div class="p-4 border-2 border-benzGrey-300 rounded-lg bg-benzGrey-50">
          <h4 class="font-medium mb-3">Neue Adresse hinzufügen</h4>
          <app-address-form
            [addressType]="addressType"
            [editMode]="false"
            (saveAddress)="onNewAddressSaved($event)"
            (cancel)="showAddForm.set(false)">
          </app-address-form>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .radio-button-container {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 16px;
      border: 2px solid #b7b7b7;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;
      background-color: #fefefe;
    }

    .radio-button-container:hover {
      border-color: #48433e;
    }

    .radio-button-container.selected {
      border-color: #48433e;
      background-color: #fefefe;
    }

    .radio-button-container input[type="radio"] {
      width: 24px;
      height: 24px;
      margin-top: 2px;
      accent-color: #b02b2c;
      flex-shrink: 0;
    }

    .badge-standard {
      display: inline-block;
      padding: 2px 8px;
      background-color: #257809;
      color: white;
      font-size: 12px;
      border-radius: 4px;
      font-weight: 500;
    }

    .badge-label {
      display: inline-block;
      padding: 2px 8px;
      background-color: #e3f2fd;
      color: #1976d2;
      font-size: 12px;
      border-radius: 4px;
      font-weight: 500;
    }
  `]
})
export class AddressListSelectorComponent {
  @Input() addresses: Address[] = [];
  @Input() addressType: 'invoice' | 'delivery' = 'delivery';
  @Input() selectedAddress?: Address;
  @Input() showSameAsOption = false;
  
  @Output() addressSelected = new EventEmitter<Address>();
  @Output() sameAsSelected = new EventEmitter<void>();
  @Output() addressCreated = new EventEmitter<Address>();

  public authService = inject(AuthService);
  public showAddForm = signal(false);
  public useSameAddress = signal(false);

  onSelectAddress(address: Address): void {
    this.useSameAddress.set(false);
    this.addressSelected.emit(address);
  }

  onUseSameAddress(): void {
    this.useSameAddress.set(true);
    this.showAddForm.set(false);
    this.sameAsSelected.emit();
  }

  onSelectDifferentAddress(): void {
    this.useSameAddress.set(false);
    this.showAddForm.set(false);
    // Wenn es eine vorher ausgewählte Adresse gibt, diese wieder auswählen
    if (this.selectedAddress) {
      this.addressSelected.emit(this.selectedAddress);
    }
  }

  onNewAddressSaved(address: Address): void {
    this.showAddForm.set(false);
    this.addressCreated.emit(address);
  }
}

